/**
 * Notification Button - Bell icon with unread badge
 */

import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getUnreadCount, listNotifications } from '../../api/notifications';
import { NotificationDropdown } from './NotificationDropdown';
import { useNotificationWebSocket } from '../../hooks/useNotificationWebSocket';
import { useAuthContext } from '../Auth/AuthProvider';

export interface NotificationButtonProps {
  className?: string;
}

export function NotificationButton({ className = '' }: NotificationButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuthContext();
  
  // Enable WebSocket for real-time updates (hook always called, but internally checks user ID)
  useNotificationWebSocket();
  
  // Fetch unread count (backend will look up user by email for master key users)
  const { data: unreadCountFromAPI = 0, error: unreadCountError } = useQuery({
    queryKey: ['notifications', 'unread-count', user?.id, user?.email],
    queryFn: async () => {
      const result = await getUnreadCount(user?.id);
      if (import.meta.env.DEV) {
        console.log('[NotificationButton] getUnreadCount result:', result, 'for user:', { id: user?.id, email: user?.email });
      }
      return result;
    },
    refetchInterval: 60000, // Refetch every 60 seconds (1 minute)
    enabled: !!user?.id || !!user?.email, // Enable if we have user ID or email
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: 1, // Only retry once on failure
  });
  
  // Fetch recent notifications (pre-fetch when component mounts, not just when dropdown opens)
  const { data: recentNotifications, isLoading: isLoadingNotifications, error: notificationsError } = useQuery({
    queryKey: ['notifications', 'recent', user?.id, user?.email],
    queryFn: async () => {
      if (import.meta.env.DEV) {
        console.log('[NotificationButton] Fetching notifications with params:', { page: 1, page_size: 10, user_id: user?.id, user_email: user?.email });
      }
      const result = await listNotifications({ page: 1, page_size: 10, user_id: user?.id });
      if (import.meta.env.DEV) {
        console.log('[NotificationButton] listNotifications result:', {
          notifications_count: result?.notifications?.length || 0,
          total: result?.total || 0,
          unread_count: result?.unread_count || 0,
          full_result: result,
        });
      }
      // Ensure we always return a valid object
      return result || { notifications: [], total: 0, unread_count: 0, page: 1, page_size: 10 };
    },
    enabled: (!!user?.id || !!user?.email), // Always fetch when user is available (not just when dropdown opens)
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: 1, // Only retry once on failure
  });

  // Use unread_count from listNotifications as fallback if unreadCount query fails
  const unreadCount = unreadCountFromAPI || recentNotifications?.unread_count || 0;

  // Debug logging in development
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[NotificationButton] State:', {
        unreadCountFromAPI,
        unreadCountFromList: recentNotifications?.unread_count,
        finalUnreadCount: unreadCount,
        notificationsCount: recentNotifications?.notifications?.length || 0,
        isLoadingNotifications,
        unreadCountError: unreadCountError?.message,
        notificationsError: notificationsError?.message,
        user: { id: user?.id, email: user?.email, username: user?.username },
        isOpen,
        fullUserObject: user,
        recentNotificationsFull: recentNotifications,
      });
    }
  }, [unreadCountFromAPI, recentNotifications, unreadCount, isLoadingNotifications, unreadCountError, notificationsError, user, isOpen]);

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          relative p-2 rounded-lg 
          text-theme-text-secondary
          hover:bg-theme-surface-hover 
          hover:text-primary-600
          transition-all duration-200
          focus:ring-2 focus:ring-blue-500 focus:outline-none focus:ring-offset-2
          ${isOpen ? 'bg-theme-surface-hover text-primary-600' : ''}
          ${className}
        `}
        title="Notifications"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span 
            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full text-xs font-semibold px-1.5 min-w-[1.25rem] h-5 flex items-center justify-center"
            style={{ fontSize: '0.625rem' }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <NotificationDropdown
          notifications={recentNotifications?.notifications || []}
          unreadCount={unreadCount}
          isLoading={isLoadingNotifications}
          onClose={() => setIsOpen(false)}
          onViewAll={() => {
            setIsOpen(false);
            // Navigate to notification center using app's navigation pattern
            const baseUrl = window.location.pathname.split('/').slice(0, -1).join('/') || '/';
            const fullPath = baseUrl === '/' ? '/notifications' : `${baseUrl}/notifications`;
            window.history.pushState({}, '', fullPath);
            window.dispatchEvent(new PopStateEvent('popstate'));
            window.dispatchEvent(new CustomEvent('navigate', { detail: { path: '/notifications' } }));
          }}
        />
      )}
      {/* Debug error display in development */}
      {import.meta.env.DEV && (unreadCountError || notificationsError) && (
        <div className="fixed bottom-4 right-4 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg p-4 max-w-md z-50">
          <p className="text-sm font-semibold text-red-800 dark:text-red-200 mb-2">Notification API Errors:</p>
          {unreadCountError && (
            <p className="text-xs text-red-600 dark:text-red-300 mb-1">
              Unread Count: {unreadCountError.message || String(unreadCountError)}
            </p>
          )}
          {notificationsError && (
            <p className="text-xs text-red-600 dark:text-red-300">
              Notifications: {notificationsError.message || String(notificationsError)}
            </p>
          )}
        </div>
      )}
    </>
  );
}

